import { IOpenedAppModal } from "@mctypes/frontend.types";
import { AppModalEnums } from "../mc-shared/sharedConstants";
import { IApp, INotification, IProjectDeprecated, IUserDepricated } from "@mctypes/global.types";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import Axios from "axios";
import * as R from "ramda";
import { openapi } from "../../openapi";
import { apiSlice, getCacheKey } from "../apiSlice";
import { logicWhenAppStarts } from "../app/actions";
import { resetIssueRedux } from "../app/routes/issuesApp/issueSlice";
import { doMarkAllNotificationsAsRead, doToggleNotificationAsRead } from "../app/userApi";
import { AppThunk, IRootState } from "../store";
import { set } from "lodash";
import { setHashQueryParam } from "../urlHash";

interface ISuccessMessage {
  show: boolean;
  text: string;
}

export interface IGlobalReducer {
  user: IUserDepricated;
  successMessage: ISuccessMessage;
  failedMessage: {
    show: boolean;
    text: string;
  };
  projects: IProjectDeprecated[];
  isFullScreen: boolean;
  apps: IApp[];
  isProduction: boolean;
  openedAppModals: IOpenedAppModal[];
  isPrintMode: boolean;
  reloadAll: boolean;
  memberIdModal: string | null;
  hasUnsavedComment?: boolean;
  currentProjectId: string | null;
  showNotifications: boolean;
  showUserSettingsModal: boolean;
  xApiKey: string | null;
}

const initialState: IGlobalReducer = {
  user: null,
  successMessage: {
    show: false,
    text: "",
  },
  failedMessage: {
    show: true,
    text: "",
  },
  projects: [],
  isFullScreen: false,
  apps: [],
  isProduction: null,
  openedAppModals: [],
  isPrintMode: false,
  reloadAll: false,
  hasUnsavedComment: false,
  memberIdModal: null,
  currentProjectId: null,
  showNotifications: false,
  showUserSettingsModal: false,
  xApiKey: localStorage.getItem("xApiKey"), // 👈 här
};

export const setShowSuccessMessageThunk =
  (text: string): AppThunk =>
  (dispatch) => {
    dispatch(
      setShowSuccessMessageInRedux({
        show: true,
        text,
      }),
    );

    setTimeout(() => {
      dispatch(setShowSuccessMessageInRedux({ show: false, text: "" }));
    }, 3000);
  };

export const setShowFailedMessageThunk =
  (text: string): AppThunk =>
  (dispatch) => {
    dispatch(
      setShowFailedMessageInRedux({
        show: true,
        text,
      }),
    );

    setTimeout(() => {
      dispatch(setShowFailedMessageInRedux({ show: false, text: "" }));
    }, 3000);
  };

export const addAppModalThunk =
  (openedAppModal: IOpenedAppModal): AppThunk =>
  (dispatch, getState) => {
    const { itemId } = openedAppModal;
    if (getState().globalReducer.openedAppModals.find((appModal) => appModal.itemId === itemId)) {
      console.warn("modal for id is already opened");
      return;
    }
    const resolvedModal =
      openedAppModal.app === AppModalEnums.ISSUE
        ? { ...openedAppModal, app: AppModalEnums.ISSUE_MANTINE }
        : openedAppModal;

    dispatch(addAppModalInRedux(resolvedModal));
    setHashQueryParam("id", itemId);
  };

export const toggleNotificationAndReplaceNotificationThunk =
  (notificationId: string): AppThunk =>
  async (dispatch, getState) => {
    const copyOfNotifications = R.clone(getState().globalReducer.user.notifications);
    const updatedNotification = await doToggleNotificationAsRead(notificationId);
    const notificationIndex = copyOfNotifications?.findIndex((notification) => notification._id === notificationId);

    if (notificationIndex === -1) {
      console.warn("index is -1 notification");
      return;
    }
    copyOfNotifications[notificationIndex] = updatedNotification;

    console.log(copyOfNotifications[notificationIndex]);
    dispatch(setNotificationInRedux(copyOfNotifications));
  };

export const toggleAllNotificationsAsReadThunk = (): AppThunk => async (dispatch) => {
  const updatedNotifications = await doMarkAllNotificationsAsRead();
  dispatch(setNotificationInRedux(updatedNotifications));
};

export const thunkReloadAppThunk =
  (history): AppThunk =>
  async (dispatch) => {
    dispatch(apiSlice.util.resetApiState());
    dispatch(resetIssueRedux());

    dispatch(logicWhenAppStarts(history));
    dispatch(setReloadAllInRedux(true));
    setTimeout(() => {
      dispatch(setReloadAllInRedux(false));
    }, 20);
  };

export const loadProjectsThunk = (): AppThunk => async (dispatch) => {
  try {
    const projectsCall = await Axios.get("/api/user/projects");
    const projects = projectsCall.data;
    dispatch(setProjectsInRedux(projects));
  } catch (err) {
    await Axios.get("/api/logout");
    alert("Failed to load projects. Login and try again");
  }
};

export const logoutThunk = (): AppThunk => (dispatch) => {
  Axios.get(`/api/logout`);
  dispatch(setUserInRedux(null));
  window.location.href = "#/login";
};

export const globalSlice = createSlice({
  name: "checklist",
  initialState,
  reducers: {
    setUserInRedux: (state, action: PayloadAction<IUserDepricated>) => {
      state.user = action.payload;
    },
    setProjectsInRedux: (state, action: PayloadAction<IProjectDeprecated[]>) => {
      state.projects = action.payload;
    },
    setShowSuccessMessageInRedux: (state, action: PayloadAction<ISuccessMessage>) => {
      state.successMessage = action.payload;
    },
    setShowFailedMessageInRedux: (state, action: PayloadAction<{ show: boolean; text: string }>) => {
      state.failedMessage = action.payload;
    },
    setAppsInRedux: (state, action: PayloadAction<IApp[]>) => {
      state.apps = action.payload;
    },
    setIsProductionInRedux: (state, action: PayloadAction<boolean>) => {
      state.isProduction = action.payload;
    },
    addAppModalInRedux: (state, action: PayloadAction<IOpenedAppModal>) => {
      state.openedAppModals.push(action.payload);
    },
    removeAppModalInRedux: (state, action: PayloadAction<string>) => {
      const index = state.openedAppModals.findIndex((modal) => modal.itemId === action.payload);
      if (index === -1) {
        return;
      }
      state.openedAppModals.splice(index, 1);
    },
    setNotificationInRedux: (state, action: PayloadAction<INotification[]>) => {
      state.user.notifications = action.payload;
    },
    setPrintModeInRedux: (state, action: PayloadAction<boolean>) => {
      state.isPrintMode = action.payload;
    },
    setReloadAllInRedux: (state, action: PayloadAction<boolean>) => {
      state.reloadAll = action.payload;
    },
    setMemberIdModalInRedux: (state, action: PayloadAction<string | null>) => {
      state.memberIdModal = action.payload;
    },
    setHasUnsavedCommentInRedux: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedComment = action.payload;
    },
    setCurrentProjectIdInRedux: (state, action: PayloadAction<string | null>) => {
      state.currentProjectId = action.payload;
    },
    setShowNotificationsInRedux: (state, action: PayloadAction<boolean>) => {
      state.showNotifications = action.payload;
    },
    setShowUserSettingsModalInRedux: (state, action: PayloadAction<boolean>) => {
      state.showUserSettingsModal = action.payload;
    },
    setXApiKeyInRedux: (state, action: PayloadAction<string | null>) => {
      state.xApiKey = action.payload;

      if (action.payload) {
        localStorage.setItem("xApiKey", action.payload);
      } else {
        localStorage.removeItem("xApiKey");
      }
    },
  },
});

// Reusable empty constants so we don't allocate [] on every call
const EMPTY_APPS: IApp[] = [] as unknown as IApp[];

// ---- pure input selectors (no map/filter/spread/new objects)
const selectProject = (s: IRootState): IProjectDeprecated | null => s.adminReducer.project ?? null;

const selectAllApps = (s: IRootState): IApp[] => s.globalReducer.apps ?? EMPTY_APPS;

const selectUserId = (s: IRootState): string | undefined => s.globalReducer.user?._id;

// ---- memoized selector
export const getEnabledApps = createSelector(
  [selectProject, selectAllApps, selectUserId],
  (project, allApps, userId) => {
    if (!project) return EMPTY_APPS;

    const memberGroup = project.members?.find((m) => (m.user as IUserDepricated)._id === userId)?.group ?? null;

    return allApps.filter((app) => {
      const projectApp = project.apps?.find((e) => e.app === app.name);
      if (!projectApp || projectApp.active === false) return false;

      if (app.enabledInProjects?.length) {
        return app.enabledInProjects.includes(project.maconomy.projectNo);
      }

      if (memberGroup === "ADMIN" || memberGroup === "INTERNAL") return true;
      if (memberGroup === "PARTNER") return !!projectApp.access?.partner;
      if (memberGroup === "CLIENT") return !!projectApp.access?.client;
      return false;
    });
  },
);

export const selectIsUserMutilconsult = createSelector(
  (state: IRootState) => state.globalReducer.user,
  (user: IUserDepricated) => user?.email.toLowerCase().endsWith("@multiconsult.no") === true,
);

export const selectUserMember = createSelector(
  (state: IRootState) => state.adminReducer.project,
  (state: IRootState) => state.globalReducer.user,
  (project: IProjectDeprecated, user: IUserDepricated): IProjectMemberApiItem | null => {
    if (project == null || user == null) {
      return null;
    }
    return project.members.find((member) => (member.user as IUserDepricated)?._id === user._id);
  },
);

export const {
  setUserInRedux,
  setShowSuccessMessageInRedux,
  setShowFailedMessageInRedux,
  setProjectsInRedux,
  setAppsInRedux,
  setIsProductionInRedux,
  addAppModalInRedux,
  removeAppModalInRedux,
  setNotificationInRedux,
  setPrintModeInRedux,
  setReloadAllInRedux,
  setMemberIdModalInRedux,
  setHasUnsavedCommentInRedux,
  setCurrentProjectIdInRedux,
  setShowNotificationsInRedux,
  setShowUserSettingsModalInRedux,
  setXApiKeyInRedux,
} = globalSlice.actions;

export default globalSlice.reducer;

const enhancedApi = openapi.enhanceEndpoints({
  endpoints: {
    getUserApikey: {
      providesTags: ["UserApikey"],
    },
    postUserApikey: {
      invalidatesTags: ["UserApikey"],
    },
    deleteUserApikey: {
      invalidatesTags: ["UserApikey"],
    },
    putProjectMemberOtherAttributes: {
      invalidatesTags: ["TrpcMembers"],
    },
    getProjectMembers: {
      providesTags: ["TrpcMembers"],
    },
    putProjectMember: {
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        const respbody = await queryFulfilled;
        const updatedMember = respbody.data;
        const queries = getState().trpc.queries;
        const cacheKey = getCacheKey(queries, "getProjectMembers");

        const patchResult = dispatch(
          openapi.util.updateQueryData("getProjectMembers", cacheKey, (draft) => {
            draft.map((member) => {
              if (member._id === resp.body.memberId) {
                Object.assign(member, updatedMember);
              }
              return member;
            });
          }),
        );
        dispatch(setShowSuccessMessageThunk("Lagret endring på medlem"));
      },
    },
    getWhoami: {
      providesTags: ["Whoami"],
    },
    putUserProfile: {
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        const respbody = await queryFulfilled;
        const updatedUser = respbody.data;
        const queries = getState().trpc.queries;
        const cacheKey = getCacheKey(queries, "getProjectMembers");
        dispatch(openapi.util.invalidateTags(["Whoami"]));

        const patchResult = dispatch(
          openapi.util.updateQueryData("getProjectMembers", cacheKey, (draft) => {
            draft.map((member) => {
              if (member.user._id === updatedUser._id) {
                member.user = {
                  ...member.user,
                  ...updatedUser,
                };
              }
              return member;
            });
          }),
        );
        dispatch(setShowSuccessMessageThunk("Lagret endring på bruker"));
      },
    },
  },
});
